import { useAuth0 } from "@auth0/auth0-react";
import MerchantDomainBase from "entities/domain/admin/merchants/merchant-domain";
import AgentDomain from "entities/domain/agents/agent-domain";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import { useMerchantQuery } from "hooks/queries/use-settings-merchant";
import { useMerchantAgents } from "hooks/queries/use-teammates";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setIsSelectorOpened, setMerchantId } from "redux/features/merchant";
import { useAppDispatch } from "redux/hooks";
import { endSession } from "util/endSession";
import { isNotFoundError, isSessionExpiredError } from "util/httpErrors";
import { LOCAL_STORAGE_SELECTED_MERCHANT_ID_KEY } from "util/localStorage";

interface MerchantResolutionOptions {
  // The merchant id this route already knows about (URL param for
  // MerchantPrivateRoute, last-selected id for AbstractPrivateRoute). It's
  // fetched in parallel with /agents/me so the merchant request doesn't wait on
  // the agent response to come back before it starts.
  candidateMerchantId?: number;
  // Whether this route may adopt the candidate merchant in place rather than
  // redirecting to the canonical /:merchantId URL.
  allowInPlaceAdoption: boolean;
  // Whether adopting the candidate should persist it as the selected merchant.
  persistSelectionOnAdopt: boolean;
  // Whether a failed merchant fetch while adopting should log the user out.
  logoutOnMerchantError: boolean;
}

interface MerchantResolution {
  currentAgent?: AgentDomain;
  merchant?: MerchantDomainBase | null;
}

const parseStoredMerchantId = (value: string | null): number | undefined =>
  value ? parseInt(value, 10) : undefined;

const isMerchantAvailable = (
  agent: AgentDomain,
  merchantId: number | undefined
): boolean =>
  merchantId !== undefined && agent.merchants.some((m) => m.id === merchantId);

/**
 * Shared merchant-selection logic for the private route guards. Resolves which
 * merchant the current route should run as, given the logged-in agent: adopt a
 * known-good candidate in place, redirect to the canonical /:merchantId URL,
 * auto-select the only merchant, or open the account selector.
 */
export function useMerchantResolution(
  options: MerchantResolutionOptions
): MerchantResolution {
  const {
    candidateMerchantId,
    allowInPlaceAdoption,
    persistSelectionOnAdopt,
    logoutOnMerchantError,
  } = options;

  const location = useLocation();
  const navigate = useNavigate();
  const auth0Context = useAuth0();
  const dispatch = useAppDispatch();

  const { data: currentAgent, error: currentAgentError } = useCurrentAgent();
  const { data: merchant, isError: merchantIsError } =
    useMerchantQuery(candidateMerchantId);

  // Warm the merchant-agents cache as soon as we know the candidate merchant,
  // so it fetches in parallel with the merchant itself instead of waiting for
  // `merchant` to resolve in PrivateRoute. Same query key as PrivateRoute's
  // `useMerchantAgents(merchant.id)`, so it dedupes into one request. Disabled
  // automatically when the candidate is undefined/NaN (e.g. a merchantless URL).
  useMerchantAgents(candidateMerchantId);

  const isPublicPath = location.pathname.includes("public");
  const canAdoptCandidate =
    !!currentAgent &&
    allowInPlaceAdoption &&
    isMerchantAvailable(currentAgent, candidateMerchantId);

  // Log out if the agent lookup itself 404s, or if the session can no longer
  // produce a valid token. Without this the route guards wait on `currentAgent`
  // forever and the agent is stuck on a spinner; ending the session sends them
  // back through withAuthenticationRequired to a fresh login.
  useEffect(() => {
    if (isPublicPath) return;
    if (
      !isNotFoundError(currentAgentError) &&
      !isSessionExpiredError(currentAgentError)
    )
      return;

    endSession(auth0Context.logout);
  }, [isPublicPath, currentAgentError, auth0Context]);

  // Resolve which merchant this route runs as, once the agent has loaded. This
  // is a single decision with mutually exclusive outcomes, so it lives in one
  // effect: there's no derived-flag invariant for a later edit to break, and no
  // way for "adopt" and "fall back" to both act in the same pass.
  useEffect(() => {
    if (isPublicPath || !currentAgent) return;

    // 1. Adopt the candidate merchant in place when allowed and it belongs to
    //    the agent (it was fetched in parallel with /agents/me).
    if (canAdoptCandidate) {
      if (merchant) {
        dispatch(setMerchantId({ id: merchant.id, country: merchant.country }));

        if (persistSelectionOnAdopt && candidateMerchantId !== undefined) {
          localStorage.setItem(
            LOCAL_STORAGE_SELECTED_MERCHANT_ID_KEY,
            candidateMerchantId.toString()
          );
        }
      } else if (merchantIsError && logoutOnMerchantError) {
        endSession(auth0Context.logout);
      }

      return;
    }

    // 2. Redirect to the last selected merchant if it's still available.
    const storedMerchantId = localStorage.getItem(
      LOCAL_STORAGE_SELECTED_MERCHANT_ID_KEY
    );

    if (
      isMerchantAvailable(currentAgent, parseStoredMerchantId(storedMerchantId))
    ) {
      // Client-side navigation, NOT window.location.href: a hard nav here would
      // reload the whole SPA (re-parse/-execute the bundle, re-init Auth0, tear
      // down the socket/providers) — a second full boot on essentially every
      // login. Navigating in place lands on MerchantPrivateRoute, which adopts
      // the merchant without any of that.
      navigate(`/${storedMerchantId}${location.pathname}${location.search}`, {
        replace: true,
      });

      return;
    }

    // 3. Auto-select the only merchant the agent has.
    if (currentAgent.merchants.length === 1) {
      const onlyMerchant = currentAgent.merchants[0];
      localStorage.setItem(
        LOCAL_STORAGE_SELECTED_MERCHANT_ID_KEY,
        onlyMerchant.id.toString()
      );
      navigate(`/${onlyMerchant.id}${location.pathname}${location.search}`, {
        replace: true,
      });

      return;
    }

    // 4. Otherwise let the agent pick.
    dispatch(setIsSelectorOpened(true));
  }, [
    isPublicPath,
    currentAgent,
    canAdoptCandidate,
    candidateMerchantId,
    persistSelectionOnAdopt,
    logoutOnMerchantError,
    merchant,
    merchantIsError,
    location.pathname,
    location.search,
    dispatch,
    navigate,
    auth0Context,
  ]);

  return { currentAgent, merchant };
}
